import { create } from 'zustand';

/**
 * Clock-drift store — tracks how far this device's `Date.now()` is from
 * the authoritative server clock. The `X-Server-Time` header on every
 * API response feeds this; surfaces (banner, invoice save button, etc.)
 * read `driftMs` to decide whether to warn or block.
 *
 * The threshold of 5 minutes mirrors the backend's `ClientTimeMiddleware`
 * (`MAX_CLIENT_DRIFT_MS = 15 min`) with safety margin. Operators with a
 * < 5 min drift see nothing; 5–15 min get a banner; > 15 min block POST
 * mutations until they fix the clock (the server would discard
 * `X-Client-Time` and may downstream-reject anyway).
 */
export const CLOCK_DRIFT_WARN_MS = 5 * 60 * 1000;
export const CLOCK_DRIFT_BLOCK_MS = 15 * 60 * 1000;

interface ClockDriftState {
  /** ms = (server - client). Positive means client is BEHIND server. */
  driftMs: number;
  /** Absolute drift; >= 0. */
  absDriftMs: number;
  lastServerMs: number | null;
  lastCheckedMs: number | null;
  shouldWarn: boolean;
  shouldBlock: boolean;
  record: (serverMs: number) => void;
  reset: () => void;
}

export const useClockDriftStore = create<ClockDriftState>((set) => ({
  driftMs: 0,
  absDriftMs: 0,
  lastServerMs: null,
  lastCheckedMs: null,
  shouldWarn: false,
  shouldBlock: false,
  record: (serverMs: number) => {
    const now = Date.now();
    let driftMs = serverMs - now;
    // Smoke-test override — `?fakeDriftMin=7` in the URL forces a
    // drift so the banner is visible on localhost (where the server
    // and the browser share the same OS clock and a real drift
    // never appears). Production users never have this param in
    // their URL bar, so leaving the override ungated is safe — the
    // earlier `process.env.NODE_ENV` check tripped on Jest + on
    // Vite configs that don't inline NODE_ENV.
    if (typeof window !== 'undefined') {
      const fake = new URLSearchParams(window.location.search).get(
        'fakeDriftMin'
      );
      const n = fake ? Number(fake) : NaN;
      if (Number.isFinite(n)) {
        driftMs = n * 60 * 1000;
         
        console.warn(
          `[clock-drift] fakeDriftMin override active: ${n} min → ${driftMs} ms`
        );
      }
    }
    const absDriftMs = Math.abs(driftMs);
    set({
      driftMs,
      absDriftMs,
      lastServerMs: serverMs,
      lastCheckedMs: now,
      shouldWarn: absDriftMs >= CLOCK_DRIFT_WARN_MS,
      shouldBlock: absDriftMs >= CLOCK_DRIFT_BLOCK_MS,
    });
  },
  reset: () =>
    set({
      driftMs: 0,
      absDriftMs: 0,
      lastServerMs: null,
      lastCheckedMs: null,
      shouldWarn: false,
      shouldBlock: false,
    }),
}));
